import React from 'react';
import { 
  UserProfile, 
  FinancialConfig, 
  Quote, 
  Material, 
  Client, 
  Supplier, 
  WorkOrder, 
  Receipt, 
  Appointment, 
  StaffMember, 
  StorefrontProfile, 
  AppView 
} from '../../types';
import { 
  InventoryBatch, 
  StockMovement, 
  ClinicalPatient, 
  ClinicalConsultation, 
  ClinicalVaccination, 
  ClinicalExamVault, 
  AccountantAccess,
  ReceptionOrderItem
} from '../../modules/erp/types';
import { EnterpriseSuiteLayout, EnterpriseCommandCenter, EnterpriseGuard } from '../../modules/erp/enterprise';
import { EnterpriseVerticalId } from '../../modules/erp/enterprise/config/enterpriseVerticals';
import { resolveCompanyName, resolveCompanyProfile } from '../../hooks/useCompanyProfile';
import { useTeamPermissions } from '../../contexts/TeamPermissionsContext';

// Lazy loading sob demanda dos módulos Enterprise
const InventoryHubManager = React.lazy(() => import('../../modules/erp/enterprise/inventory/InventoryHubManager'));
const ReceptionPosManager = React.lazy(() => import('../../modules/erp/enterprise/reception/ReceptionPosManager'));
const PatientManager = React.lazy(() => import('../../modules/erp/enterprise/clinical/PatientManager'));
const SoapConsultationEditor = React.lazy(() => import('../../modules/erp/enterprise/clinical/SoapConsultationEditor'));
const VaccineRetentionCenter = React.lazy(() => import('../../modules/erp/enterprise/clinical/VaccineRetentionCenter'));
const ExamVaultUploader = React.lazy(() => import('../../modules/erp/enterprise/clinical/ExamVaultUploader'));
const AccountantClosingHub = React.lazy(() => import('../../modules/erp/enterprise/accounting/AccountantClosingHub').then(m => ({ default: m.AccountantClosingHub })));
const AccountantPortalView = React.lazy(() => import('../../modules/erp/enterprise/accounting/AccountantPortalView').then(m => ({ default: m.AccountantPortalView })));
const IceCreamSpeedPos = React.lazy(() => import('../../modules/erp/enterprise/reception/IceCreamSpeedPos'));
const FinancialHubManager = React.lazy(() => import('../../modules/erp/enterprise/finance/FinancialHubManager'));
const IndModuleRoot = React.lazy(() => import('../../modules/erp/ind/IndModuleRoot'));

export function EnterpriseLoadingFallback() {
  return (
    <div className="min-h-100 flex flex-col items-center justify-center p-8 space-y-3 font-sans">
      <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin" />
      <span className="text-xs font-black uppercase tracking-wider text-zinc-500">
        Carregando Módulo Plano Empresa...
      </span>
    </div>
  );
}

export interface EnterpriseViewDispatcherProps {
  view: AppView;
  user: UserProfile | null;
  config: FinancialConfig | null;
  isEnterpriseUser: boolean;
  enterpriseVertical: EnterpriseVerticalId;
  quotes: Quote[];
  workOrders: WorkOrder[];
  receipts: Receipt[];
  materials: Material[];
  clients: Client[];
  suppliers: Supplier[];
  appointments: Appointment[];
  staffMembers: StaffMember[];
  storefrontProfile: StorefrontProfile | null;
  inventoryBatches: InventoryBatch[];
  stockMovements: StockMovement[];
  clinicalPatients: ClinicalPatient[];
  clinicalConsultations: ClinicalConsultation[];
  clinicalVaccinations: ClinicalVaccination[];
  clinicalExams: ClinicalExamVault[];
  accountantAccesses: AccountantAccess[];
  currentMonthQuotesCount: number;
  activeConsultationPatient: ClinicalPatient | null;
  setActiveConsultationPatient: (p: ClinicalPatient | null) => void;
  isAccountantPortalMode: boolean;
  setIsAccountantPortalMode: (mode: boolean) => void;
  onGoBack: () => void;
  onNavigate: (view: AppView) => void;
  onOpenChat: () => void;
  onOpenWorkOrders: () => void;
  onOpenEstoqueHub: () => void;
  onOpenProntuarioSoap: () => void;
  onOpenRetornoVacinas: () => void;
  onOpenCofreExames: () => void;
  onOpenPortalContador: () => void;
  onOpenClients: () => void;
  onOpenProfile: () => void;
  onOpenProductCatalog: () => void;
  onOpenTeamModal: () => void;
  onSelectEnterpriseVertical: (v: EnterpriseVerticalId) => void;
  onRequestUpgrade: (featureName: string, featureDesc: string) => void;
  onLogout: () => Promise<void>;
  onSaveInventoryBatch: (b: InventoryBatch) => Promise<any>;
  onDeleteInventoryBatch: (id: string) => Promise<void>;
  onSaveStockMovement: (m: StockMovement) => Promise<any>;
  onSaveMaterials: (m: Material[]) => Promise<void>;
  onSaveSupplier: (s: Supplier) => Promise<any>;
  onSaveClinicalPatient: (p: ClinicalPatient) => Promise<any>;
  onDeleteClinicalPatient: (id: string) => Promise<void>;
  onSaveClinicalConsultation: (c: ClinicalConsultation) => Promise<any>;
  onSaveClinicalVaccination: (v: ClinicalVaccination) => Promise<any>;
  onDeleteClinicalVaccination: (id: string) => Promise<void>;
  onSaveClinicalExam: (e: ClinicalExamVault) => Promise<any>;
  onDeleteClinicalExam: (id: string) => Promise<void>;
  onInviteAccountant: (data: { email: string; name?: string; firm?: string; crc?: string }) => Promise<any>;
  onRevokeAccountant: (id: string) => Promise<void>;
  onExecuteConsultationCheckout: (consultationId: string, items: any[], paymentMethod: string, amount: number) => Promise<any>;
  onExecuteReceptionCheckout: (checkoutData: {
    consultationId?: string;
    clientId: string;
    clientName: string;
    clientPhone?: string;
    patientId?: string;
    patientName?: string;
    items: ReceptionOrderItem[];
    totalAmount: number;
    paymentMethod: 'pix' | 'cartao_credito' | 'cartao_debito' | 'dinheiro' | 'misto';
    paymentDetails?: any;
    consumedBatches: Array<{
      materialId: string;
      materialName: string;
      batchId?: string;
      batchCode?: string;
      quantity: number;
      unitCost: number;
    }>;
  }) => Promise<{ receiptNumber: string }>;
  onRecordVaccineWhatsAppSent: (vaccineId: string) => void;
}

export const EnterpriseViewDispatcher: React.FC<EnterpriseViewDispatcherProps> = ({
  view,
  user,
  config,
  isEnterpriseUser,
  enterpriseVertical,
  quotes,
  workOrders,
  receipts,
  materials,
  clients,
  suppliers,
  appointments,
  staffMembers,
  storefrontProfile,
  inventoryBatches,
  stockMovements,
  clinicalPatients,
  clinicalConsultations,
  clinicalVaccinations,
  clinicalExams,
  accountantAccesses,
  currentMonthQuotesCount,
  activeConsultationPatient,
  setActiveConsultationPatient,
  isAccountantPortalMode,
  setIsAccountantPortalMode,
  onGoBack,
  onNavigate,
  onOpenChat,
  onOpenWorkOrders,
  onOpenEstoqueHub,
  onOpenProntuarioSoap,
  onOpenRetornoVacinas,
  onOpenCofreExames,
  onOpenPortalContador,
  onOpenClients,
  onOpenProfile,
  onOpenProductCatalog,
  onOpenTeamModal,
  onSelectEnterpriseVertical,
  onRequestUpgrade,
  onLogout,
  onSaveInventoryBatch,
  onDeleteInventoryBatch,
  onSaveStockMovement,
  onSaveMaterials,
  onSaveSupplier,
  onSaveClinicalPatient,
  onDeleteClinicalPatient,
  onSaveClinicalConsultation,
  onSaveClinicalVaccination,
  onDeleteClinicalVaccination,
  onSaveClinicalExam,
  onDeleteClinicalExam,
  onInviteAccountant,
  onRevokeAccountant,
  onExecuteConsultationCheckout,
  onExecuteReceptionCheckout,
  onRecordVaccineWhatsAppSent,
}) => {
  const { can } = useTeamPermissions();
  const effectiveTier = isEnterpriseUser 
    ? 'enterprise' 
    : (user?.plan === 'Especialista' || user?.isTrialActive) 
      ? 'premium' 
      : user?.plan === 'Pro' 
        ? 'pro' 
        : 'free';

  const renderWithEnterpriseSuite = (content: React.ReactNode) => {
    if (!user) return <>{content}</>;
    return (
      <EnterpriseSuiteLayout
        user={user}
        config={config}
        currentView={view}
        vertical={enterpriseVertical}
        onSelectVertical={onSelectEnterpriseVertical}
        onNavigate={(target) => onNavigate(target)}
        onQuickBudget={onOpenChat}
        onLogout={onLogout}
        onOpenHelp={() => onOpenProfile()}
        onOpenTeamModal={onOpenTeamModal}
        onOpenProfile={() => onOpenProfile()}
        onNewQuote={onOpenChat}
        onNewWorkOrder={onOpenWorkOrders}
        onNewBatch={onOpenEstoqueHub}
        onNewClient={onOpenClients}
        badges={{
          quotesCount: currentMonthQuotesCount,
          openWorkOrdersCount: workOrders.filter(w => w.status === 'open' || w.status === 'in_progress').length,
          expiringBatchesCount: inventoryBatches.filter(b => {
            if (!b.expirationDate) return false;
            const d = new Date(b.expirationDate);
            return !isNaN(d.getTime()) && d <= new Date(Date.now() + 15 * 24 * 60 * 60 * 1000) && (b.currentQuantity || 0) > 0;
          }).length,
          overdueVaccinesCount: clinicalVaccinations.filter(v => v.nextDueDate && new Date(v.nextDueDate) < new Date() && v.status !== 'completed').length
        }}
      >
        {content}
      </EnterpriseSuiteLayout>
    );
  };

  // Modo Kiosk / Balcão 100% da tela: Frente de caixa imersiva
  if (view === 'speed_pos') {
    const activeUser = user || ({
      id: 'local_terminal_operator',
      fullName: 'Operador',
      ownerName: 'Operador',
      businessName: '',
      plan: 'Grátis',
      isTrialActive: false,
      isMasterUser: false
    } as any);

    const profile = resolveCompanyProfile(activeUser, 'Minha Sorveteria');

    const content = (
      <EnterpriseGuard
        currentTier={effectiveTier}
        featureName="Speed-Checkout: Sorveteria & Açaí por Quilo"
        featureDescription="Frente de caixa ultra-rápida com balança digital Web Serial, controle de taras de recipientes e persistência Local-First."
        onUpgrade={() => {
          onRequestUpgrade('Speed-Checkout & Balanças Seriais', 'Pesagem contínua via Web Serial, gestão de taras e terminal PDV local-first.');
        }}
        onBack={onGoBack}
      >
        <React.Suspense fallback={<EnterpriseLoadingFallback />}>
          <IceCreamSpeedPos
            userId={activeUser.id}
            businessName={profile.businessName}
            pixKey={profile.pixKey}
            pixKeyType={profile.pixKeyType}
            baseHourValue={config?.baseHourValue || 0}
            monthlyFixedCost={config?.monthlyFixedCost || 0}
            proLaboreGoal={config?.proLaboreGoal || 0}
            onBack={onGoBack}
          />
        </React.Suspense>
      </EnterpriseGuard>
    );
    return <div className="h-dvh max-h-screen w-full overflow-hidden bg-zinc-50">{content}</div>;
  }

  // Se o usuário é Enterprise e está na tela Home ou Erp-Hub, renderiza o EnterpriseCommandCenter
  if ((view === 'home' || view === 'erp-hub') && user && config && isEnterpriseUser) {
    return renderWithEnterpriseSuite(
      <EnterpriseCommandCenter
        user={user}
        config={config}
        vertical={enterpriseVertical}
        quotes={quotes}
        workOrders={workOrders}
        receipts={receipts}
        inventoryBatches={inventoryBatches}
        stockMovements={stockMovements}
        clinicalPatients={clinicalPatients}
        clinicalVaccinations={clinicalVaccinations}
        clinicalConsultations={clinicalConsultations}
        accountantAccesses={accountantAccesses}
        onNavigate={(target) => onNavigate(target)}
        onNewQuote={onOpenChat}
        onNewWorkOrder={onOpenWorkOrders}
        onNewBatch={onOpenEstoqueHub}
        onNewPatient={onOpenProntuarioSoap}
        onOpenTeamModal={onOpenTeamModal}
      />
    );
  }

  if (view === 'estoque_hub' && user) {
    if (!can('view_cost_center') && !can('view_margin')) {
      return (
        <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4 font-sans">
          <div className="max-w-md w-full bg-white border border-zinc-200 rounded-3xl p-6 text-center space-y-4 shadow-xl">
            <h2 className="text-xl font-bold text-zinc-900">Acesso Restrito</h2>
            <p className="text-zinc-500">Seu perfil n�o tem permiss�o para acessar o Estoque e Custos.</p>
            <button onClick={onGoBack} className="mt-4 px-6 py-2 bg-emerald-600 text-white rounded-xl font-semibold">Voltar</button>
          </div>
        </div>
      );
    }
    const content = (
      <EnterpriseGuard
        currentTier={effectiveTier}
        featureName="Gestão de Estoque & Lotes"
        featureDescription="Balancete financeiro, lotes por validade e extrato de movimentos."
        onUpgrade={() => {
          onRequestUpgrade('Gestão de Estoque & Lotes', 'Balancete financeiro, lotes por validade e extrato de movimentos.');
        }}
        onBack={onGoBack}
      >
        <React.Suspense fallback={<EnterpriseLoadingFallback />}>
          <InventoryHubManager
            userId={user?.id || (user as any)?.userId}
            batches={inventoryBatches}
            movements={stockMovements}
            suppliers={suppliers}
            materials={materials}
            onSaveBatch={onSaveInventoryBatch}
            onDeleteBatch={onDeleteInventoryBatch}
            onSaveMovement={onSaveStockMovement}
            onSaveMaterials={onSaveMaterials}
            onSaveSupplier={onSaveSupplier}
            onBack={onGoBack}
            onNavigateToCatalog={onOpenProductCatalog}
          />
        </React.Suspense>
      </EnterpriseGuard>
    );
    return isEnterpriseUser ? renderWithEnterpriseSuite(content) : (
      <div className="min-h-screen bg-zinc-50 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">{content}</div>
    );
  }

  if (view === 'prontuario_soap' && user) {
    const content = (
      <EnterpriseGuard
        currentTier={effectiveTier}
        featureName="Prontuário Eletrônico SOAP Legal"
        featureDescription="Anamnese, sinais vitais, hipótese diagnóstica e conduta com imutabilidade jurídica e receita em PDF."
        onUpgrade={() => {
          onRequestUpgrade('Prontuário SOAP & CRM de Pacientes', 'Ficha médica imutável, receituário e checkout integrado.');
        }}
        onBack={onGoBack}
      >
        <React.Suspense fallback={<EnterpriseLoadingFallback />}>
          {activeConsultationPatient ? (
            <SoapConsultationEditor
              patient={activeConsultationPatient}
              existingConsultation={clinicalConsultations.find(c => c.patientId === activeConsultationPatient.id && c.status === 'in_progress') || null}
              availableBatches={inventoryBatches}
              user={user}
              staffMembers={staffMembers}
              onSaveConsultation={onSaveClinicalConsultation}
              onExecuteCheckout={onExecuteConsultationCheckout}
              onBack={() => setActiveConsultationPatient(null)}
            />
          ) : (
            <PatientManager
              patients={clinicalPatients}
              clients={clients}
              consultations={clinicalConsultations}
              vaccinations={clinicalVaccinations}
              exams={clinicalExams}
              onSavePatient={onSaveClinicalPatient}
              onDeletePatient={onDeleteClinicalPatient}
              onStartConsultation={(pat) => setActiveConsultationPatient(pat)}
              onBack={onGoBack}
            />
          )}
        </React.Suspense>
      </EnterpriseGuard>
    );
    return isEnterpriseUser ? renderWithEnterpriseSuite(content) : (
      <div className="min-h-screen bg-zinc-50 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">{content}</div>
    );
  }

  if (view === 'retorno_vacinas' && user) {
    const content = (
      <EnterpriseGuard
        currentTier={effectiveTier}
        featureName="Central de Retorno de Vacinas no WhatsApp"
        featureDescription="Disparo nativo via Deep Link com pré-filtro da agenda online e agrupamento combo de múltiplos pets do mesmo tutor."
        onUpgrade={() => {
          onRequestUpgrade('Central de Retorno de Vacinas', 'Lembretes inteligentes via WhatsApp nativo sem custos de API.');
        }}
        onBack={onGoBack}
      >
        <React.Suspense fallback={<EnterpriseLoadingFallback />}>
          <VaccineRetentionCenter
            vaccinations={clinicalVaccinations}
            patients={clinicalPatients}
            clients={clients}
            storeSlug={storefrontProfile?.slug || 'clinica-saude'}
            storeName={resolveCompanyName(user, 'Minha Clínica')}
            onSaveVaccination={onSaveClinicalVaccination}
            onDeleteVaccination={onDeleteClinicalVaccination}
            onRecordWhatsAppSent={onRecordVaccineWhatsAppSent}
            onBack={onGoBack}
          />
        </React.Suspense>
      </EnterpriseGuard>
    );
    return isEnterpriseUser ? renderWithEnterpriseSuite(content) : (
      <div className="min-h-screen bg-zinc-50 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">{content}</div>
    );
  }

  if (view === 'cofre_exames' && user) {
    const content = (
      <EnterpriseGuard
        currentTier={effectiveTier}
        featureName="Cofre de Exames & Imagens DICOM"
        featureDescription="Guarda legal de 5 anos (CFMV), compressão client-side WebP e visualizador com janela óssea."
        onUpgrade={() => {
          onRequestUpgrade('Cofre de Exames & DICOM R2', 'Arquivamento em nuvem com conformidade normativa e régua milimétrica.');
        }}
        onBack={onGoBack}
      >
        <React.Suspense fallback={<EnterpriseLoadingFallback />}>
          <ExamVaultUploader
            exams={clinicalExams}
            patients={clinicalPatients}
            user={user}
            onSaveExam={onSaveClinicalExam}
            onDeleteExam={onDeleteClinicalExam}
            onBack={onGoBack}
          />
        </React.Suspense>
      </EnterpriseGuard>
    );
    return isEnterpriseUser ? renderWithEnterpriseSuite(content) : (
      <div className="min-h-screen bg-zinc-50 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">{content}</div>
    );
  }

  if (view === 'portal_contador' && user) {
    if (!can('view_executive_cockpit')) {
      return (
        <div className="min-h-screen bg-zinc-50 flex items-center justify-center p-4 font-sans">
          <div className="max-w-md w-full bg-white border border-zinc-200 rounded-3xl p-6 text-center space-y-4 shadow-xl">
            <h2 className="text-xl font-bold text-zinc-900">Acesso Restrito</h2>
            <p className="text-zinc-500">Apenas gerentes ou contadores podem acessar o Portal do Contador.</p>
            <button onClick={onGoBack} className="mt-4 px-6 py-2 bg-emerald-600 text-white rounded-xl font-semibold">Voltar</button>
          </div>
        </div>
      );
    }
    const content = (
      <EnterpriseGuard
        currentTier={effectiveTier}
        featureName="Portal do Contador Dedicado (Forma B)"
        featureDescription="Acesso exclusivo para auditoria fiscal com compliance LGPD e exportação para Domínio e Alterdata."
        onUpgrade={() => {
          onRequestUpgrade('Portal do Contador', 'Acesso externo para contabilidade com total isolamento de prontuários.');
        }}
        onBack={onGoBack}
      >
        <React.Suspense fallback={<EnterpriseLoadingFallback />}>
          {isAccountantPortalMode ? (
            <AccountantPortalView
              receipts={receipts}
              stockMovements={stockMovements}
              consultations={clinicalConsultations}
              clinicName={resolveCompanyName(user, 'Minha Empresa')}
              onBackToClinic={() => setIsAccountantPortalMode(false)}
            />
          ) : (
            <AccountantClosingHub
              receipts={receipts}
              stockMovements={stockMovements}
              consultations={clinicalConsultations}
              accountantAccesses={accountantAccesses}
              clinicName={resolveCompanyName(user, 'Minha Empresa')}
              onInviteAccountant={onInviteAccountant}
              onRevokeAccountant={onRevokeAccountant}
              onSwitchToAccountantView={() => setIsAccountantPortalMode(true)}
            />
          )}
        </React.Suspense>
      </EnterpriseGuard>
    );
    return isEnterpriseUser ? renderWithEnterpriseSuite(content) : (
      <div className="min-h-screen bg-zinc-50 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">{content}</div>
    );
  }

  if (view === 'recepcao_pdv' && user) {
    const isVet = user.niche?.toLowerCase().includes('pet') || user.niche?.toLowerCase().includes('vet');
    const isMedical = user.niche?.toLowerCase().includes('medic') || user.niche?.toLowerCase().includes('saude') || user.niche?.toLowerCase().includes('estet');
    const isFashion = user.niche?.toLowerCase().includes('roupa') || user.niche?.toLowerCase().includes('moda') || user.niche?.toLowerCase().includes('boutique');
    
    const posFeatureName = isVet 
      ? 'Balcão da Recepção & Checkout Pet'
      : isMedical
        ? 'Balcão da Recepção & Checkout Clínico'
        : isFashion
          ? 'Balcão de Vendas & Bipador USB'
          : 'Frente de Caixa & Checkout';

    const posFeatureDescription = isVet || isMedical
      ? 'Fila de atendimento em tempo real, comanda unificada com produtos de balcão e PIX dinâmico.'
      : 'Frente de caixa ágil com controle de vendas, comanda de itens e PIX dinâmico.';

    const content = (
      <EnterpriseGuard
        currentTier={effectiveTier}
        featureName={posFeatureName}
        featureDescription={posFeatureDescription}
        onUpgrade={() => {
          onRequestUpgrade(posFeatureName, 'Fechamento de vendas e atendimentos, controle de itens e baixa automática de estoque.');
        }}
        onBack={onGoBack}
      >
        <React.Suspense fallback={<EnterpriseLoadingFallback />}>
          <ReceptionPosManager
            user={user}
            clients={clients}
            patients={clinicalPatients}
            consultations={clinicalConsultations}
            batches={inventoryBatches}
            materials={materials}
            appointments={appointments}
            onExecuteCheckout={onExecuteReceptionCheckout}
            onScheduleNextAppointment={() => {
              onNavigate('calendar');
            }}
            onBack={onGoBack}
          />
        </React.Suspense>
      </EnterpriseGuard>
    );
    return isEnterpriseUser ? renderWithEnterpriseSuite(content) : (
      <div className="min-h-screen bg-zinc-50 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">{content}</div>
    );
  }

  if (view === 'financial_hub' && user) {
    const content = (
      <EnterpriseGuard
        currentTier={effectiveTier}
        featureName="Contas a Pagar e Receber"
        featureDescription="Gestão de títulos financeiros, fluxo de caixa, liquidações e plano de contas da empresa."
        onUpgrade={() => {
          onRequestUpgrade('Contas a Pagar e Receber', 'Controle financeiro de despesas, receitas, fluxo de caixa e quitações.');
        }}
        onBack={onGoBack}
      >
        <React.Suspense fallback={<EnterpriseLoadingFallback />}>
          <FinancialHubManager
            user={user}
            inventoryBatches={inventoryBatches}
            onBack={onGoBack}
            onOpenReceipts={() => onNavigate('receipts')}
          />
        </React.Suspense>
      </EnterpriseGuard>
    );
    return isEnterpriseUser ? renderWithEnterpriseSuite(content) : (
      <div className="min-h-screen bg-zinc-50 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">{content}</div>
    );
  }

  if (view === 'industria' && user) {
    const content = (
      <EnterpriseGuard
        currentTier={effectiveTier}
        featureName="Indústria de Transformação & PCP"
        featureDescription="Chão de fábrica, rastreabilidade física, SGQ ISO 9001, fichas técnicas multi-nível, apontamentos e MRP I."
        onUpgrade={() => {
          onRequestUpgrade('Indústria & PCP', 'Controle completo de chão de fábrica, SGQ e ordens de produção.');
        }}
        onBack={onGoBack}
      >
        <React.Suspense fallback={<EnterpriseLoadingFallback />}>
          <IndModuleRoot
            user={user}
            onBack={onGoBack}
          />
        </React.Suspense>
      </EnterpriseGuard>
    );
    return isEnterpriseUser ? renderWithEnterpriseSuite(content) : (
      <div className="min-h-screen bg-zinc-50 max-w-7xl mx-auto">{content}</div>
    );
  }

  return null;
};
